import React, { useState, useEffect, useCallback } from 'react';
import { FirmContext, FirmContextType } from './FirmContext.definition';
import { useAuth } from './AuthContext';
import { subscribeToFirm, getFirm } from '@/services/firmService';
import { Firm } from '@/types';
import { logger } from '@/utils/logger';

// NOTE: useFirm is exported from FirmContext.definition.ts to avoid Fast Refresh warnings

interface FirmProviderProps {
    children: React.ReactNode;
}

export function FirmProvider({ children }: FirmProviderProps) {
    const { effectiveFirmId, initializing: authInitializing } = useAuth();

    const [firm, setFirm] = useState<Firm | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Function to manually refresh firm data if needed (though subscription handles most cases)
    const refreshFirm = useCallback(async () => {
        if (!effectiveFirmId) return;

        try {
            const firmData = await getFirm(effectiveFirmId);
            if (firmData) {
                setFirm(firmData);
            }
        } catch (err) {
            logger.error('Error refreshing firm data manually', err);
        }
    }, [effectiveFirmId]);

    useEffect(() => {
        // If auth is still initializing, don't do anything yet
        if (authInitializing) return;

        if (!effectiveFirmId) {
            setFirm(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        // Subscribe to firm updates
        const unsubscribe = subscribeToFirm(
            effectiveFirmId,
            (newFirmData) => {
                setFirm(newFirmData);
                setLoading(false);
            },
            (err) => {
                logger.error('Error in firm subscription', err);
                setError(err);
                setLoading(false);
            }
        );

        return () => {
            unsubscribe();
        };
    }, [effectiveFirmId, authInitializing]);

    const value: FirmContextType = {
        firm,
        loading,
        error,
        refreshFirm,
    };

    return <FirmContext.Provider value={value}>{children}</FirmContext.Provider>;
}
